import { drawTradeConnections } from './trading-game-trades.js';
import { setupGameSubscription, gameAuthHeaders } from './trading-game-subscription.js';
import { TradingRound, candlePath, reviewRound, commitmentOptions, gradeCommitment, habit, HABITS } from './trading-game-engine.js';

function initTradingGame() {
const root = document.querySelector('#crypto-game');
if (root) {
  const $ = id => document.getElementById(`cg-${id}`);
  const priceFormats = [2, 4, 6].map(digits => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: digits }));
  const money = n => priceFormats[0].format(n);
  const signed = n => `${n >= 0 ? '+' : '−'}${money(Math.abs(n))}`;
  const percent = n => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  let request;
  let round, charts = 1, timer, generation = 0, easy = false, superEasy = false, musicOn = false;
  let commitment = null, activeCommitment = null;
  const stop = () => { cancelAnimationFrame(timer); timer = null; };
  if (new URLSearchParams(location.search).get('test') === 'true') {
    window.tradingGameTest = { TradingRound, candlePath, reviewRound, commitmentOptions, gradeCommitment };
  }
  const bestKey = () => `bitbank-crypto-game-v1-${charts}${superEasy ? '-super-easy' : easy ? '-easy' : ''}`;
  const mystery = i => `Market ${String.fromCharCode(65 + i)}`;
  const audio = $('audio'); audio.volume = .28;
  async function playMusic() {
    if (!musicOn || round?.state !== 'running') return;
    try { await audio.play(); if (!musicOn || round?.state !== 'running') audio.pause(); $('audio-status').textContent = ''; }
    catch { if (!musicOn || round?.state !== 'running') return; musicOn = false; $('music').textContent = '♫ Music off'; $('music').setAttribute('aria-pressed', 'false'); $('audio-status').textContent = 'Music unavailable. You can keep playing.'; }
  }
  $('music').addEventListener('click', () => {
    musicOn = !musicOn; $('music').textContent = musicOn ? '♫ Music on' : '♫ Music off'; $('music').setAttribute('aria-pressed', String(musicOn));
    if (musicOn) playMusic(); else audio.pause();
    if (round?.state === 'running') $('charts').children[round.selected].focus({preventScroll:true});
  });
  $('fullscreen').addEventListener('click', async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); else await root.requestFullscreen(); }
    catch { $('audio-status').textContent = 'Fullscreen is unavailable in this browser.'; }
  });
  document.addEventListener('fullscreenchange', () => { $('fullscreen').textContent = document.fullscreenElement ? '⛶ Exit fullscreen' : '⛶ Fullscreen'; $('fullscreen').setAttribute('aria-label', document.fullscreenElement ? 'Exit fullscreen' : 'Enter fullscreen'); render(); });
  $('easy').addEventListener('click', () => { if (['running','paused'].includes(round?.state)) return; superEasy = false; easy = !easy; $('super-easy').textContent = '✦ Super Easy · 7-day forecasts · Pro'; $('easy').setAttribute('aria-pressed', String(easy)); prepare(); });
  setupGameSubscription(root, {
    pause,
    enable: () => {
      superEasy = true; easy = true;
      $('easy').setAttribute('aria-pressed', 'false');
      $('super-easy').textContent = '✦ Super Easy · 7-day forecasts · On';
      prepare();
    }
  });
  const ratio = v => v === null ? 'N/A' : v.toFixed(2);
  const pp = n => `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(2)} pp`;
  const bars = n => `${Number(n).toFixed(1)} bars`;
  const emptyRecord = () => ({ rounds: 0, best: null, streak: 0, lastPct: null });
  let storageBlocked = false, record = emptyRecord();
  function loadRecord() {
    try {
      const raw = JSON.parse(localStorage.getItem(`${bestKey()}-record`) || 'null');
      if (!raw || !Number.isFinite(raw.rounds) || raw.rounds < 0) return emptyRecord();
      return { rounds: Math.floor(raw.rounds), best: Number.isFinite(raw.best) ? raw.best : null,
        streak: Number.isFinite(raw.streak) ? Math.max(0, Math.floor(raw.streak)) : 0, lastPct: Number.isFinite(raw.lastPct) ? raw.lastPct : null };
    } catch { storageBlocked = true; return emptyRecord(); }
  }
  function best() {
    record = loadRecord();
    const mode = charts === 1 ? '1-chart' : charts === 2 ? '2-chart' : '4-chart';
    $('best').textContent = storageBlocked ? 'Personal best unavailable in this browser.'
      : record.best === null ? 'Your next personal best is waiting.'
      : `Personal best · ${percent(record.best)} · ${record.rounds} ${record.rounds === 1 ? 'round' : 'rounds'} in ${mode} mode${record.streak > 1 ? ` · better than my last round ${record.streak} rounds running` : ''}`;
  }
  function saveRecord() { try { localStorage.setItem(`${bestKey()}-record`, JSON.stringify(record)); } catch { /* Storage is optional. */ } }
  const habitTitles = { observer: 'Observer', reactive: 'Reactive', patient: 'Patient', cutter: 'Late exits', cautious: 'Mostly flat', active: 'Active' };
  const commitmentText = { 'beat-best': graded => `Finished ${percent(graded.actual)} against a ${percent(graded.target)} bar.`,
    patient: graded => `${graded.actual} closed trades against a limit of ${graded.target}.`,
    'beat-hold': graded => `Your edge over buy & hold was ${pp(graded.actual)}.` };
  const sameCommitment = (a, b) => !!a && !!b && a.id === b.id && (a.id !== 'beat-best' || a.targetPct === b.targetPct);
  function renderCommit() {
    for (const button of $('commit').children) button.setAttribute('aria-pressed', String(sameCommitment(commitment, { id: button.dataset.commit, targetPct: Number(button.dataset.target) })));
  }
  function createCharts() {
    $('charts').replaceChildren();
    $('charts').dataset.count = String(charts);
    for (let i = 0; i < charts; i++) {
      const symbol = mystery(i);
      const panel = document.createElement('div');
      panel.className = 'cg-chart';
      panel.tabIndex = 0;
      panel.setAttribute('role', 'button');
      panel.setAttribute('aria-label', `${symbol} chart. Hold space or press and hold to buy; release to sell.`);
      panel.innerHTML = `<div class="cg-chart-head"><span class="cg-coin">${String.fromCharCode(65+i)}</span><strong>${symbol}</strong><small>1H</small><span class="cg-price">—</span></div><canvas role="img" aria-label="Revealed hourly candlesticks for ${symbol}"></canvas><div class="cg-chart-foot"><span class="cg-forecast-label">MYSTERY PAIR</span><span class="cg-chart-hint">HOLD TO BUY</span></div>`;
      panel.addEventListener('focus', () => { round?.select(i); render(); });
      panel.addEventListener('pointerdown', e => {
        if (e.button !== 0 || !round || round.state !== 'running') return;
        e.preventDefault();
        round.select(i);
        if (round.buy(`pointer-${e.pointerId}`)) panel.setPointerCapture(e.pointerId);
        render();
      });
      const release = e => { round?.sell(`pointer-${e.pointerId}`); render(); };
      panel.addEventListener('pointerup', release);
      panel.addEventListener('pointercancel', release);
      panel.addEventListener('lostpointercapture', release);
      // Capture keeps release reliable; leaving the chart also closes the trade.
      panel.addEventListener('pointermove', e => {
        const b = panel.getBoundingClientRect();
        if (e.clientX < b.left || e.clientX > b.right || e.clientY < b.top || e.clientY > b.bottom) release(e);
      });
      panel.addEventListener('contextmenu', e => e.preventDefault());
      $('charts').append(panel);
    }
  }
  function canvasContext(canvas) {
    const box = canvas.getBoundingClientRect(), ratio = Math.min(devicePixelRatio || 1, 2);
    const width = Math.round(box.width * ratio), height = Math.round(box.height * ratio);
    if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
    const ctx = canvas.getContext('2d'); ctx.setTransform(ratio, 0, 0, ratio, 0, 0); ctx.clearRect(0, 0, box.width, box.height);
    return { ctx, w: box.width, h: box.height };
  }
  function drawMarket(canvas, market, marketIndex) {
    const { ctx, w, h } = canvasContext(canvas);
    if (!w || !h) return;
    const right = w < 230 ? 44 : w < 300 ? 51 : 70;
    const left = 14, top = 23, bottom = h - 24;
    const candles = market ? market.candles.slice(Math.max(0, round.displayIndex - 49), round.index + 1) : [];
    if (round?.live) candles.push(round.live[marketIndex]);
    const preview = market ? round.forecast(marketIndex) : [];
    const low = candles.length ? Math.min(...candles.map(c => c.low), ...preview.map(p => p.low_band || p.close)) : 0;
    const high = candles.length ? Math.max(...candles.map(c => c.high), ...preview.map(p => p.high_band || p.close)) : 1;
    const pad = Math.max((high - low) * .15, high * .002);
    const min = low - pad, max = high + pad;
    const y = price => top + (max - price) / (max - min) * (bottom - top);
    ctx.font = w < 300 ? '9px system-ui' : '10px system-ui';
    for (let i = 0; i <= 4; i++) {
      const yy = top + (bottom - top) * i / 4;
      ctx.strokeStyle = '#293a43'; ctx.setLineDash([2, 5]); ctx.beginPath(); ctx.moveTo(left, yy); ctx.lineTo(w - right, yy); ctx.stroke();
      if (candles.length) { ctx.fillStyle = '#91a7b1'; ctx.fillText((max - (max - min) * i / 4).toLocaleString('en-US', { maximumFractionDigits: high < 10 ? 4 : 1 }), w - right + 7, yy + 3); }
    }
    ctx.setLineDash([]);
    if (!candles.length) return;
    const step = (w - right - left) / (easy ? 58 : 51);
    const first = round.displayIndex - candles.length + 1;
    candles.forEach((c, i) => {
      const x = left + (i + .5) * step, up = c.close >= c.open;
      ctx.strokeStyle = ctx.fillStyle = up ? '#9bdac3' : '#dba197';
      ctx.beginPath(); ctx.moveTo(x, y(c.high)); ctx.lineTo(x, y(c.low)); ctx.stroke();
      ctx.fillRect(x - step * .29, Math.min(y(c.open), y(c.close)), Math.max(1, step * .58), Math.max(1.5, Math.abs(y(c.open) - y(c.close))));
    });
    if (preview.length) {
      const x = p => left + (candles.length - .5 + (p.timestamp - candles.at(-1).timestamp) / 3600 / (superEasy ? 28 : 1)) * step;
      if (preview.every(p => p.low_band > 0 && p.high_band >= p.close && p.low_band <= p.close)) {
        ctx.fillStyle = '#baabef33'; ctx.beginPath();
        preview.forEach((p,i) => { if (!i) ctx.moveTo(x(p),y(p.low_band)); else ctx.lineTo(x(p),y(p.low_band)); });
        [...preview].reverse().forEach(p => ctx.lineTo(x(p), y(p.high_band))); ctx.closePath(); ctx.fill();
      }
      if (!superEasy) {
        preview.forEach((p, i) => {
          const open = i ? preview[i - 1].close : candles.at(-1).close;
          ctx.fillStyle = '#c9b9fb44'; ctx.strokeStyle = '#c9b9fbbb'; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.moveTo(x(p), y(p.high_band || p.close)); ctx.lineTo(x(p), y(p.low_band || p.close)); ctx.stroke();
          const yy = Math.min(y(open), y(p.close)), height = Math.max(3, Math.abs(y(open) - y(p.close)));
          ctx.fillRect(x(p) - step * .29, yy, step * .58, height); ctx.strokeRect(x(p) - step * .29, yy, step * .58, height);
        });
      }
      ctx.strokeStyle = '#c9b9fb'; ctx.lineWidth = 2; ctx.setLineDash([4,3]); ctx.beginPath();
      ctx.moveTo(left + (candles.length - .5)*step, y(candles.at(-1).close));
      preview.forEach(p => ctx.lineTo(x(p),y(p.close))); ctx.stroke(); ctx.setLineDash([]); ctx.lineWidth = 1;
      ctx.fillStyle = '#c9b9fb'; ctx.fillText(`+${(preview.at(-1).timestamp-candles.at(-1).timestamp)/3600}h`, Math.min(x(preview.at(-1))-8,w-right-20), h-6);
    }
    const currentY = y(candles.at(-1).close);
    ctx.strokeStyle = '#9bdac377'; ctx.setLineDash([4, 4]); ctx.beginPath(); ctx.moveTo(left, currentY); ctx.lineTo(w - right, currentY); ctx.stroke(); ctx.setLineDash([]);
    if (round.position?.market === marketIndex) {
      ctx.strokeStyle = '#d8d4aa'; ctx.setLineDash([5, 5]); ctx.beginPath(); ctx.moveTo(left, y(round.position.entry)); ctx.lineTo(w - right, y(round.position.entry)); ctx.stroke(); ctx.setLineDash([]);
    }
    drawTradeConnections(ctx, round.trades, round.position, {
      market: marketIndex, price: round.price(marketIndex), index: round.displayIndex,
      fraction: round.live ? round.progress / round.data.interval_ms : 1,
      first, step, left, right: w - right, top, bottom, y, format: signed
    });
    ctx.fillStyle = '#91a7b1'; ctx.fillText('Past', left, h - 6); ctx.textAlign = 'right'; ctx.fillText('Now', Math.min(left + (candles.length - .5) * step, w - right - 20), h - 6); ctx.textAlign = 'left';
  }
  function drawJourney() {
    const { ctx, w, h } = canvasContext($('journey'));
    const values = round?.journey || [10000];
    const min = Math.min(10000, ...values), max = Math.max(10000, ...values), spread = Math.max(max - min, 10);
    const y = v => h - 9 - (v - min) / spread * (h - 18);
    ctx.strokeStyle = '#3c515a'; ctx.setLineDash([3, 5]); ctx.beginPath(); ctx.moveTo(0, y(10000)); ctx.lineTo(w, y(10000)); ctx.stroke(); ctx.setLineDash([]);
    ctx.strokeStyle = values.at(-1) >= 10000 ? '#a4e2cb' : '#eda99f'; ctx.lineWidth = 2; ctx.beginPath();
    values.forEach((v, i) => { const x = i / 120 * w; if (i === 0) ctx.moveTo(x, y(v)); else ctx.lineTo(x, y(v)); }); ctx.stroke();
  }
  function render() {
    root.dataset.state = round?.state || 'ready'; root.dataset.holding = String(!!round?.position);
    const equity = round?.equity ?? 10000, pnl = equity - 10000;
    $('equity').textContent = money(equity);
    $('pnl').replaceChildren(document.createTextNode(`${signed(pnl)} `));
    const pct = document.createElement('small'); pct.textContent = percent(pnl / 100); $('pnl').append(pct);
    $('pnl').className = pnl >= 0 ? 'cg-positive' : 'cg-negative';
    const elapsed = round ? round.elapsed + round.progress / round.data.interval_ms : 0;
    const remaining = Math.ceil((120 - elapsed) * (round?.data.interval_ms || 550) / 1000);
    $('time').textContent = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
    $('progress').style.width = `${elapsed / 120 * 100}%`;
    $('pause').disabled = !['running','paused'].includes(round?.state);
    $('pause').textContent = round?.state === 'paused' ? '▶ Play' : 'Ⅱ Pause';
    $('pause').setAttribute('aria-label', round?.state === 'paused' ? 'Resume game' : 'Pause game');
    $('easy').disabled = ['running','paused'].includes(round?.state);
    document.querySelectorAll('[data-charts]').forEach(b => { b.disabled = ['running', 'paused'].includes(round?.state); });
    $('trades').textContent = `${round?.trades.length || 0} closed trades`;
    $('position').textContent = round?.position ? `● Holding ${mystery(round.position.market)} · ${signed(round.equity - round.position.cost)}` : round?.state === 'finished' ? '○ All positions closed' : round?.state === 'paused' ? 'Ⅱ Paused · position closed' : '○ In cash';
    [...$('charts').children].forEach((panel, i) => {
      panel.classList.toggle('selected', (round?.selected || 0) === i);
      panel.classList.toggle('holding', round?.position?.market === i);
      panel.setAttribute('aria-pressed', String(round?.position?.market === i));
      const preview = round?.forecast(i) || [];
      panel.querySelector('.cg-forecast-label').textContent = easy ? preview.length ? (superEasy ? '7-day Bitbank trading forecasts' : '3hr Bitbank trading forecasts') : 'NO ARCHIVED FORECAST THIS HOUR' : 'MYSTERY PAIR';
      panel.querySelector('.cg-forecast-label').title = easy ? round?.forecastSource === 'replay' ? 'Three-bar replay estimate from revealed prices only; not an archived live forecast. Shading shows estimated uncertainty.' : 'Archived live-issued Chronos2 forecasts for Poloniex, replayed over Binance history.' : 'Pair revealed at the finish';
      panel.querySelector('.cg-price').textContent = round ? priceFormats[round.price(i) < 1 ? 2 : round.price(i) < 10 ? 1 : 0].format(round.price(i)) : '—';
      panel.querySelector('.cg-chart-hint').textContent = round?.position?.market === i ? 'HOLDING · RELEASE TO SELL' : (round?.selected || 0) === i ? 'SELECTED · HOLD TO BUY' : 'HOLD TO BUY';
      drawMarket(panel.querySelector('canvas'), round?.data.markets[i], i);
    });
    drawJourney();
    $('target').hidden = !commitment;
    if (commitment) $('target').textContent = `THIS ROUND'S BAR · ${commitment.label}`;
  }
  function start() {
    activeCommitment = commitment;
    if (!round || !['ready', 'paused'].includes(round.state)) return;
    round.start(); $('intro').hidden = true; $('result').hidden = true;
    playMusic();
    stop();
    let last = performance.now();
    function frame(now) {
      if (round?.state !== 'running') return;
      if (now - last >= 1000 / 30) {
        round.advance(Math.min(now - last, 100)); last = now; render();
        if (round.state === 'finished') { finish(); return; }
      }
      timer = requestAnimationFrame(frame);
    }
    timer = requestAnimationFrame(frame);
    render(); $('charts').children[round.selected].focus({ preventScroll: true });
  }
  function pause() { if (round?.state === 'running') { stop(); round.pause(); audio.pause(); render(); } }
  async function prepare(autoStart = false) {
    const version = ++generation, mode = { charts, easy, superEasy };
    request?.abort();
    request = new AbortController();
    const controller = request;
    const timeout = setTimeout(() => controller.abort(), 20000);
    stop(); audio.pause(); cancelAnimationFrame(confettiFrame); $('confetti').hidden = true; round = null; $('intro').hidden = false; $('result').hidden = true;
    $('start').disabled = true; $('load-status').textContent = 'Finding a moment in market history…';
    best(); createCharts(); render();
    try {
      const response = await fetch(`/api/trading-game/round?charts=${mode.charts}${mode.easy ? '&easy=1' : ''}${mode.superEasy ? '&super_easy=1' : ''}`, { headers: mode.superEasy ? gameAuthHeaders() : {}, cache: 'no-store', signal: request.signal });
      const data = await response.json();
      if (version !== generation) return;
      if (!response.ok) throw Error(data.error || 'History is unavailable. Please try again.');
      if (data.markets?.length !== mode.charts || Boolean(data.super_easy) !== mode.superEasy) throw Error('Round did not match the selected charts. Please retry.');
      // Easy is a free local forecast view, including older round responses
      // without mode metadata. Paid mode still requires the server's flag.
      data.easy = mode.easy;
      round = new TradingRound(data);
      createCharts(); render(); $('load-status').textContent = ''; $('start').textContent = `Play ${round.data.bars * round.data.interval_ms / 1000} seconds ↗`; $('start').disabled = false;
      if (autoStart) start();
    } catch (error) {
      if (version !== generation) return;
      round = null;
      $('load-status').textContent = error.name === 'AbortError' ? 'History took too long to load. Please retry.' : error.message;
      $('start').textContent = 'Retry loading ↻'; $('start').disabled = false;
    } finally { clearTimeout(timeout); }
  }
  let shareText = '';
  let confettiFrame;
  function celebrate() {
    cancelAnimationFrame(confettiFrame);
    const canvas = $('confetti');
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    canvas.hidden = false;
    const ctx = canvas.getContext('2d'), width = innerWidth, height = innerHeight;
    const ratio = Math.min(devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio); ctx.scale(ratio, ratio);
    const colors = ['#35ed9b', '#c9a7ff', '#ff7690', '#ffdc78', '#65d8ff', '#fff5db'];
    const pieces = Array.from({length: width < 600 ? 140 : 240}, (_, i) => {
      const side = i % 2, burst = Math.floor(i / 60);
      return { x: side ? width + 8 : -8, y: height * (.65 + Math.random() * .2),
        vx: (side ? -1 : 1) * (width * .18 + Math.random() * Math.min(width * .65, 720)),
        vy: -(height * .45 + Math.random() * height * .55), delay: burst * 240,
        angle: Math.random() * Math.PI * 2, spin: (Math.random() - .5) * 12,
        size: 4 + Math.random() * 5, shape: i % 3, color: colors[i % colors.length] };
    });
    const began = performance.now(); let last = began;
    function frame(now) {
      const dt = Math.min((now - last) / 1000, .05), age = now - began; last = now;
      ctx.clearRect(0, 0, width, height); ctx.globalAlpha = Math.max(0, Math.min(1, (5000 - age) / 1200));
      for (const p of pieces) {
        if (age < p.delay) continue;
        p.vx *= Math.exp(-.55 * dt); p.vy += height * .38 * dt;
        p.x += p.vx * dt; p.y += p.vy * dt; p.angle += p.spin * dt;
        ctx.save(); ctx.translate(p.x + Math.sin(age / 180 + p.angle) * 3, p.y); ctx.rotate(p.angle);
        ctx.scale(1, .35 + Math.abs(Math.cos(age / 220 + p.angle)) * .65); ctx.fillStyle = p.color;
        if (p.shape === 0) { ctx.beginPath(); ctx.arc(0, 0, p.size * .55, 0, Math.PI * 2); ctx.fill(); }
        else if (p.shape === 1) ctx.fillRect(-p.size / 2, -p.size, p.size, p.size * 2);
        else { ctx.beginPath(); for (let k = 0; k < 10; k++) { const r = k % 2 ? p.size * .4 : p.size; const a = k * Math.PI / 5; ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r); } ctx.closePath(); ctx.fill(); }
        ctx.restore();
      }
      if (age < 5000) confettiFrame = requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, width, height); canvas.hidden = true; }
    }
    confettiFrame=requestAnimationFrame(frame);
  }
  function finish() {
    stop(); audio.pause(); celebrate();
    const pnl = round.equity - 10000;
    const pct = pnl / 100;
    const review = reviewRound(round);
    const previous = record.lastPct;
    record = { rounds: record.rounds + 1, best: record.best === null ? pct : Math.max(record.best, pct),
      streak: previous === null ? 0 : pct > previous ? record.streak + 1 : 0, lastPct: pct };
    saveRecord(); best();
    $('target').hidden = true;
    $('score').textContent = `${signed(pnl)} (${percent(pct)})`; $('score').className = pnl >= 0 ? 'cg-positive' : 'cg-negative';
    const mode = charts === 1 ? 'Classic' : charts === 2 ? 'Split' : 'Expert';
    $('summary').textContent = `${money(round.equity)} final equity · ${review.trades.length} ${review.trades.length === 1 ? 'trade' : 'trades'}, ${review.wins} profitable · ${mode}${superEasy ? ' + Super Easy forecasts' : easy ? ' + Easy forecasts' : ''}`;
    $('metrics').replaceChildren(...[['Round return', percent(review.returnPct)], ['Buy & hold basket', percent(review.buyHoldPct)],
      ['Your edge vs hold', pp(review.excessPct)], ['Max drawdown', `${review.maxDrawdownPct.toFixed(2)}%`],
      ['Win rate', review.trades.length ? `${review.winRatePct.toFixed(0)}%` : '—'], ['Sharpe · Sortino', `${ratio(review.sharpe)} · ${ratio(review.sortino)}`]]
      .map(([term, value]) => { const cell = document.createElement('div'), dt = document.createElement('dt'), dd = document.createElement('dd');
        dt.textContent = term; dd.textContent = value; cell.append(dt, dd); return cell; }));
    $('habit').textContent = habitTitles[review.habit];
    $('habit-note').textContent = HABITS[review.habit];
    const evidence = [];
    if (review.best) evidence.push(['Best call', `${signed(review.best.pnl)} ${percent(review.best.pct)}`, `Trade #${review.best.n} · ${mystery(review.best.market)} · held ${bars(review.best.holdBars)}`]);
    if (review.worst && review.worst.n !== review.best?.n) evidence.push(['Worst call', `${signed(review.worst.pnl)} ${percent(review.worst.pct)}`, `Trade #${review.worst.n} · ${mystery(review.worst.market)} · held ${bars(review.worst.holdBars)}`]);
    evidence.push(['Time in the market', `${review.timeInMarketPct.toFixed(0)}%`, `A position was open for ${review.holdBars.toFixed(1)} of 120 hourly bars`]);
    if (review.trades.length) evidence.push(['Closed trades', String(review.trades.length), `${review.wins} above water, ${review.losses} below`]);
    if (review.missed) evidence.push(['Biggest move you sat out', percent(review.missed.pct), `${mystery(review.missed.market)}, hour ${review.missed.bar} of 120, while you were not in that market`]);
    if (review.trades.length && review.biggest) evidence.push(['Biggest move on the board', percent(review.biggest.pct), `${mystery(review.biggest.market)}, hour ${review.biggest.bar} of 120`]);
    $('evidence').replaceChildren(...evidence.map(([label, value, note]) => { const item = document.createElement('li');
      const head = document.createElement('span'), strong = document.createElement('strong'), small = document.createElement('small');
      head.textContent = label; strong.textContent = value; small.textContent = note;
      item.append(head, strong, small); return item; }));
    const shown = review.trades.length > 8 ? [...review.trades.slice(0, 4), ...review.trades.slice(-4)] : review.trades;
    $('tape').replaceChildren(...shown.map(t => { const row = document.createElement('div'), track = document.createElement('span'), fill = document.createElement('i');
      row.className = `cg-tape-row ${t.pnl >= 0 ? 'cg-positive' : 'cg-negative'}`;
      row.append(document.createTextNode(`#${t.n} ${mystery(t.market)}`), document.createTextNode(`${money(t.entry)} → ${money(t.exit)}`),
        document.createTextNode(`${signed(t.pnl)} ${percent(t.pct)}`));
      track.className = 'cg-tape-track'; fill.style.width = `${Math.min(100, t.holdBars / 120 * 100)}%`; track.append(fill);
      row.append(track, document.createTextNode(bars(t.holdBars))); return row; }),
      ...(review.trades.length > 8 ? [Object.assign(document.createElement('small'), { textContent: `+ ${review.trades.length - 8} more closed trades` })] : []));
    const graded = gradeCommitment(activeCommitment, review);
    $('verdict').hidden = !graded;
    if (graded) { $('verdict').textContent = `${graded.kept ? 'Bar kept' : 'Bar missed'} — ${activeCommitment.label}. ${commitmentText[graded.id](graded)}`;
      $('verdict').className = `cg-verdict ${graded.kept ? 'cg-positive' : 'cg-negative'}`; }
    const group = $('commit');
    group.replaceChildren(...commitmentOptions(record.best).map(option => { const button = document.createElement('button');
      button.type = 'button'; button.textContent = option.label; button.title = option.hint;
      button.dataset.commit = option.id; button.dataset.target = option.targetPct ?? '';
      button.setAttribute('aria-label', `${option.label}. ${option.hint}`);
      button.addEventListener('click', () => { commitment = option; renderCommit(); render();
        $('commit-status').textContent = `Next round bar: ${option.label}. ${option.hint}`; });
      return button; }));
    renderCommit();
    $('commit-status').textContent = commitment ? `Next round bar: ${commitment.label}.` : 'Pick one to carry into your next round.';
    const date = ts => new Date(ts * 1000).toISOString().slice(0, 16).replace('T', ' ');
    $('reveal').replaceChildren();
    for (const [i,m] of round.data.markets.entries()) {
      const line = document.createElement('div');
      const hold = (m.candles.at(-1).close / m.candles[35].close - 1) * 100;
      const name = document.createElement('strong'); name.textContent = `${mystery(i)} was ${m.symbol}/USDT`;
      line.append(name, document.createTextNode(`${date(m.candles[35].timestamp)} → ${date(m.candles.at(-1).timestamp)} UTC · Buy & hold: ${percent(hold)} · Your portfolio vs hold: ${percent(pct - hold)} pp`));
      $('reveal').append(line);
    }
    shareText = `I made ${signed(pnl)} (${percent(pct)}) in The Great Crypto Trading Game! ${charts}-chart${superEasy ? ' + Super Easy forecasts' : easy ? ' + Easy forecasts' : ''} mode, ${round.data.bars * round.data.interval_ms / 1000} seconds, real history, pretend money. Can you beat my score?`;
    $('share').href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent('https://bitbank.nz/the-great-crypto-trading-game')}`;
    $('copy-status').textContent = ''; $('result').hidden = false;
    $('result').focus({ preventScroll: true });
    $('result').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
  }
  $('start').addEventListener('click', () => round ? start() : prepare(true));
  $('pause').addEventListener('click', () => round?.state === 'paused' ? start() : pause());
  $('again').addEventListener('click', () => { $('charts').scrollIntoView({ block: 'center' }); prepare(true); });
  $('copy').addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(`${shareText}\nhttps://bitbank.nz/the-great-crypto-trading-game`); $('copy-status').textContent = 'Score and game link copied.'; }
    catch { $('copy-status').textContent = 'Clipboard unavailable. Use Share on X, or copy the game URL from your address bar.'; }
  });
  document.querySelectorAll('[data-charts]').forEach(button => button.addEventListener('click', () => {
    if (['running', 'paused'].includes(round?.state)) return;
    charts = Number(button.dataset.charts);
    document.querySelectorAll('[data-charts]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
    prepare();
  }));
  const editable = target => target.closest('input,textarea,select,[contenteditable=true]');
  document.addEventListener('keydown', e => {
    if (!round || $('pro').open || editable(e.target)) return;
    if (e.code === 'KeyP' && ['running', 'paused'].includes(round.state) && !e.repeat) { e.preventDefault(); round.state === 'paused' ? start() : pause(); return; }
    if (e.code === 'Space' && round.state === 'running' && !e.target.closest('button,a')) {
      e.preventDefault(); if (!e.repeat) { round.buy('keyboard'); render(); } return;
    }
    if (e.key.startsWith('Arrow') && round.state === 'running' && !e.target.closest('button,a')) {
      e.preventDefault();
      const current = round.selected, columns = charts === 1 ? 1 : 2;
      let next = current;
      if (e.key === 'ArrowLeft' && current % columns > 0) next--;
      if (e.key === 'ArrowRight' && current % columns < columns - 1) next++;
      if (e.key === 'ArrowUp') next -= columns;
      if (e.key === 'ArrowDown') next += columns;
      round.select(next); render();
    }
  });
  document.addEventListener('keyup', e => { if (e.code === 'Space') { round?.sell('keyboard'); render(); } });
  window.addEventListener('blur', pause);
  document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); });
  window.addEventListener('pagehide', pause);
  let resizeFrame = null;
  const queueResize = () => {
    if (resizeFrame !== null) return;
    resizeFrame = requestAnimationFrame(() => { resizeFrame = null; render(); });
  };
  if (typeof ResizeObserver === 'function') new ResizeObserver(queueResize).observe($('charts'));
  else window.addEventListener('resize', queueResize);
  prepare();
}

}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initTradingGame, { once: true });
else initTradingGame();
